import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, CheckCircle, Download, UserCheck, Trash2, Clock, Info } from 'lucide-react';
import api from '../services/api';

const NotificationBell = ({ userRole, onApproveInterest, recruiters = [], fetchRecruiters, onNotificationClick }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      let endpoint;
      if (userRole === 'admin') {
        endpoint = '/admin/notifications';
      } else if (userRole === 'recruiting_partner' || userRole === 'recruiter') {
        endpoint = '/recruiting/notifications';
      } else if (userRole === 'sourcing_partner') {
        endpoint = '/recruiting/notifications';
      } else if (userRole === 'candidate' || userRole === 'student') {
        endpoint = '/student/notifications';
      } else {
        endpoint = '/staff/notifications';
      }
      const response = await api.get(endpoint);
      setNotifications(response.data || []);
      setUnreadCount(response.data.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();

    // Setup SSE for real-time notifications
    const token = localStorage.getItem('token');
    if (!token) return;

    const baseURL = process.env.REACT_APP_API_URL ||
      (window.location.origin.includes(':3000')
        ? window.location.origin.replace(':3000', ':8080') + '/api'
        : `${window.location.origin}/api`);

    const sseUrl = `${baseURL}/notifications/stream?token=${token}`;
    let eventSource;
    let reconnectTimeout;
    let reconnectAttempts = 0;

    const connectSSE = () => {
      console.log(`[SSE] Connecting to ${sseUrl} (Attempt ${reconnectAttempts + 1})`);
      eventSource = new EventSource(sseUrl);

      eventSource.onopen = () => {
        console.log('[SSE] Connection established');
        reconnectAttempts = 0;
      };

      eventSource.onmessage = (event) => {
        try {
          const newNotification = JSON.parse(event.data);

          // Filter out internal connection messages
          if (newNotification.type === 'sse_connection') {
            console.log('[SSE] System message:', newNotification.message);
            return;
          }

          console.log('[SSE] Received new notification:', newNotification);
          setNotifications(prev => [newNotification, ...prev]);
          setUnreadCount(prev => prev + 1);

          if (onNotificationClick) {
            // notification handler logic if needed
          }
        } catch (err) {
          console.error('[SSE] Error parsing message:', err);
        }
      };

      eventSource.onerror = (err) => {
        console.error('[SSE] EventSource failed:', err);
        eventSource.close();

        // Exponential backoff for reconnection
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
        reconnectAttempts++;
        console.log(`[SSE] Reconnecting in ${delay}ms...`);
        reconnectTimeout = setTimeout(connectSSE, delay);
      };
    };

    connectSSE();

    return () => {
      console.log('[SSE] Cleaning up connection');
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (eventSource) eventSource.close();
    };
  }, [userRole]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const markAsRead = async (notificationId) => {
    try {
      let endpoint;
      if (userRole === 'admin') {
        endpoint = `/admin/notifications/${notificationId}/read`;
      } else if (userRole === 'recruiting_partner' || userRole === 'recruiter') {
        endpoint = `/recruiting/notifications/${notificationId}/read`;
      } else if (userRole === 'candidate' || userRole === 'student') {
        endpoint = '/student/messages/read';
        await api.put(endpoint, { message_id: notificationId });
      } else {
        endpoint = `/staff/notifications/${notificationId}/read`;
      }

      if (userRole !== 'candidate' && userRole !== 'student') {
        await api.put(endpoint);
      }

      setNotifications(notifications.map(n =>
        n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const clearAllNotifications = async () => {
    if (!window.confirm('Are you sure you want to mark all notifications as read?')) {
      return;
    }
    try {
      let endpoint;
      if (userRole === 'admin') {
        endpoint = '/admin/notifications/clear';
      } else if (userRole === 'recruiting_partner' || userRole === 'recruiter') {
        endpoint = '/recruiting/notifications/clear';
      } else {
        endpoint = '/staff/notifications/clear';
      }
      await api.delete(endpoint);
      await fetchNotifications();
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const iconClass = "h-4 w-4";
    switch (type) {
      case 'interest':
      case 'pool_interest':
        return <UserCheck className={`${iconClass} text-emerald-600`} />;
      case 'resume_download':
        return <Download className={`${iconClass} text-blue-600`} />;
      case 'recruiter_request':
      case 'request_approved':
      case 'candidates_granted':
        return <Bell className={`${iconClass} text-purple-600`} />;
      case 'sourcing_candidate_published':
      case 'sourcing_candidate_approved':
        return <CheckCircle className={`${iconClass} text-green-600`} />;
      case 'sourcing_candidate_rejected':
        return <X className={`${iconClass} text-red-600`} />;
      default:
        return <Info className={`${iconClass} text-gray-600`} />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`relative p-2.5 rounded-xl transition-all duration-200 ${showDropdown
          ? 'bg-emerald-50 text-emerald-600'
          : 'text-gray-500 hover:text-emerald-600 hover:bg-gray-50'
          }`}
      >
        <Bell className={`h-5 w-5 transition-transform duration-200 ${showDropdown ? 'scale-110' : ''}`} />
        {unreadCount > 0 && (
          <span className="absolute top-2 right-2 inline-flex items-center justify-center -mr-1 -mt-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-3 w-96 bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-100 ring-1 ring-black/5 z-50 transform origin-top-right transition-all animate-in fade-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
            <h3 className="font-bold text-gray-900 text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-xs font-bold">
                {unreadCount} new
              </span>
            )}
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
            {loading ? (
              <div className="py-12 flex justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500"></div>
              </div>
            ) : (() => {
              const unreadNotifications = notifications.filter(n => !n.is_read);
              return unreadNotifications.length === 0 ? (
                <div className="py-12 px-6 text-center">
                  <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Bell className="h-5 w-5 text-gray-400" />
                  </div>
                  <p className="text-gray-900 font-medium text-sm">All caught up!</p>
                  <p className="text-xs text-gray-500 mt-1">No new notifications</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {unreadNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => {
                        if (onNotificationClick) {
                          if (userRole === 'candidate' || userRole === 'student') {
                            if (notification.type === 'profile_message' || notification.type === 'candidate_feedback') {
                              onNotificationClick(notification.reference_id || notification.id);
                            }
                          } else {
                            // For Admin/Staff, pass the full notification object
                            onNotificationClick(notification);
                          }
                          setShowDropdown(false);
                        }
                      }}
                      className={`group px-5 py-4 hover:bg-gray-50/80 transition-colors cursor-pointer relative ${!notification.is_read ? 'bg-white' : ''
                        }`}
                    >
                      <div className="flex gap-3">
                        <div className={`mt-0.5 w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center ${!notification.is_read ? 'bg-emerald-50 ring-1 ring-emerald-100' : 'bg-gray-100'
                          }`}>
                          {getNotificationIcon(notification.type)}
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className={`text-sm leading-snug ${!notification.is_read ? 'font-semibold text-gray-900' : 'text-gray-600'
                            }`}>
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <Clock className="h-3 w-3 text-gray-400" />
                            <span className="text-xs text-gray-500 font-medium">
                              {formatTime(notification.created_at)}
                            </span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {notification.type === 'interest' && !notification.is_read && onApproveInterest && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onApproveInterest) {
                                  onApproveInterest(notification, recruiters, fetchRecruiters).then(success => {
                                    if (success) {
                                      markAsRead(notification.id);
                                      fetchNotifications();
                                    }
                                  });
                                }
                              }}
                              className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors"
                              title="Approve"
                            >
                              <UserCheck className="h-4 w-4" />
                            </button>
                          )}
                          {!notification.is_read && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-gray-100 transition-colors"
                              title="Mark as read"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {!notification.is_read && (
                        <div className="absolute left-0 top-4 bottom-4 w-0.5 bg-emerald-500 rounded-r-full"></div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Footer */}
          {notifications.filter(n => !n.is_read).length > 0 && (
            <div className="px-5 py-3 bg-gray-50/50 backdrop-blur-sm border-t border-gray-100 rounded-b-2xl flex justify-between items-center">
              <button
                onClick={clearAllNotifications}
                className="text-xs font-semibold text-gray-500 hover:text-red-600 flex items-center gap-1.5 transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Clear all
              </button>
              <button
                onClick={fetchNotifications}
                className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 transition-colors"
              >
                Refresh list
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
